import type { HostDetailRes } from '@model/host-account';
import type { Data as RegistrationData } from '@quickService/ActivitiesPublicRegistrationPage';
import type { SchedulerDataList } from '@quickService/PublicRegistrationQueryEventSchedulers';
import type { IPackageDesc, PageSchema } from '@tencent/ep-canvas-sdk/src/designer/types';
import CanvasComponent from '@tencent/ep-canvas-sdk/es/renderer';
import dynamic from 'next/dynamic';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  VFC,
} from 'react';
import ReactDOM from 'react-dom';
import Button from '@tencent/tpm-meeting-active/lib/Button';
import PlusIcon from '@tencent/tpm-meeting-active/lib/Icon/icons/PlusIcon';
import CheckIcon from '@tencent/tpm-meeting-active/lib/Icon/icons/CheckIcon';
import { usePageReport } from '../../../../../components/PageReport/use-page-report';
import { hostHook, setJudgeShowRecord } from './components/hostPanel/hooks';
import GuidePc from '@component/HomeHeader/FollowGuide/guidePc';
import classnames from 'classnames';
import RegisterButtonWithSessionSelect from './components/RegisterButtonWithSessionSelect';
import MyScheduleWidget from './components/MyScheduleWidget';
import type { OwnSchedules } from '@quickService/ActivitiesRegisterQueryUserSchedule';
import { createSimpleFromEvent } from './controller/genCanvasData';
import { getMenuList, useAccountUI } from '@component/AccoutInfo';
import { Dropdown } from '@tencent/tea-component';
import { useRouter } from 'next/router';
import CanvasCover1 from '@tencent/tpm-meeting-active/lib/CanvasCover1';
import CanvasCover2 from '@tencent/tpm-meeting-active/lib/CanvasCover2';
import '@tencent/tpm-meeting-active/lib/CanvasCover1/style';
import '@tencent/tpm-meeting-active/lib/CanvasCover2/style';
import CustomFooter from '@component/Layout/CustomFooter';
import { AppContext } from '@utils/browser/app-context';
import { UserMenu } from '@component/Layout/MobileHeader';
import { openPcLobbyPage } from '@utils/concat-meeting-util';
import { useShowLobbyBtn, skipPage } from './hooks/useShowLobbyBtn';
import { useShareInfo } from './useShare';
import { useEventGray } from '@hooks/useEventGray';
import useLobbyType from '@hooks/useLobbyType';
import useTicketList from './hooks/useTicketList';
import AppShare, { getShareData } from '../appshare';
import useLogin from '@hooks/useLogin';
import { login } from '@tencent/calendar-framework-wemeet-login';
import './CanvasRender.scss';
import LinkInvite from './components/LinkInvite';
import useViewport from '@hooks/useViewPoint';

const PageReport = dynamic(() => import('src/components/PageReport'), { ssr: false });

interface MyScheduleIconStateType {
  show: boolean;
  animated: boolean;
}
interface MyScheduleIconProviderType {
  myScheduleIconState: MyScheduleIconStateType;
  setMyScheduleIconState: (value?: any) => void;
  wxopenid: string;
  registrationData?: RegistrationData;
}
const defaultScheduleIconValue: MyScheduleIconStateType = { show: false, animated: false };
export const MyScheduleIconContext = React.createContext<MyScheduleIconProviderType>({
  myScheduleIconState: defaultScheduleIconValue,
  setMyScheduleIconState: () => null,
  wxopenid: '',
  registrationData: undefined,
});

interface IProps {
  hostData: HostDetailRes['data'];
  registrationData: RegistrationData;
  scheduler_data_list: SchedulerDataList;
  price: string;
  packages: IPackageDesc[];
  isOwner: boolean;
  schema: PageSchema;
  eventId: string;
  myScheduleList: OwnSchedules;
  wxopenid: string;
}

type CanvasData = { packages: IPackageDesc[]; schema: PageSchema };

const CanvasRender: VFC<IProps> = (props) => {
  const isNoLimit = props.registrationData.activity_basic_info.access_rule === 1;
  const [canvasData, setCanvasData] = useState<CanvasData>();
  const [myScheduleIconState, setMyScheduleIconState] =
    useState<MyScheduleIconStateType>(defaultScheduleIconValue);
  const [hasAdmissionTicket, setHasAdmissionTicket] = useState<undefined | boolean>();
  const { tickets, isAccept } = useTicketList(props.eventId, '');
  const scheduleIconValues: MyScheduleIconProviderType = {
    myScheduleIconState,
    setMyScheduleIconState,
    wxopenid: props.wxopenid,
  };

  const { isMobileUi } = useViewport(768);

  useLayoutEffect(() => {
    window.React = React;
    window.ReactDOM = ReactDOM;
  }, []);

  useEffect(() => {
    if (props.schema) return;
    // 没有 schema(首次切换到canvas但没有进去编辑过) 时从数据生成一份默认内容
    createSimpleFromEvent(props.registrationData, props.scheduler_data_list).then((data) => {
      setCanvasData(data);
    });
  }, [props.schema, props.packages]);

  const { lobbySwitch } = useEventGray({ event_id: props.eventId });

  const showLobbyBtn = useShowLobbyBtn(
    props.registrationData,
    props.myScheduleList,
    props?.eventId,
  );
  // 设置分享
  useShareInfo(props.registrationData);

  useEffect(() => {
    if (tickets?.length > 0 || isAccept) {
      const saleTicket = tickets.find(
        (item) => item.is_registed === false && item.status === 'OnSale',
      );
      if (!saleTicket?.ticket_id) {
        setHasAdmissionTicket(false);
      } else {
        setHasAdmissionTicket(true);
      }
    }
  }, [tickets, props.myScheduleList, isAccept]);

  const hostName = props.hostData?.host_detail?.host_name || '';
  const endTime = props.registrationData?.activity_basic_info?.end_time;
  const startTime = props.registrationData?.activity_basic_info?.start_time;
  const eventTitle = props.registrationData?.activity_basic_info?.title;
  const { visible, openReport, closeReport } = usePageReport();

  useEffect(() => {
    let fixedDiv: HTMLDivElement | undefined;
    const renderHoverIcon = () => {
      const pageRoot = document.querySelector('div.page-root');
      if (!pageRoot) {
        requestAnimationFrame(() => {
          renderHoverIcon();
        });
        return;
      }
      fixedDiv = document.createElement('div');
      fixedDiv.className = 'fixed-icon';
      pageRoot.appendChild(fixedDiv);
      ReactDOM.render(
        <LinkInvite
          hostName={hostName}
          startTime={startTime}
          endTime={endTime}
          eventTitle={eventTitle}
          onReportClick={openReport}
        ></LinkInvite>,
        fixedDiv,
      );
    };
    const dropHoverIcon = () => {
      if (fixedDiv) {
        ReactDOM.unmountComponentAtNode(fixedDiv);
        fixedDiv.remove();
      }
    };
    if (isMobileUi) {
      dropHoverIcon();
      return;
    }
    renderHoverIcon();
    return () => {
      dropHoverIcon();
    };
  }, [isMobileUi, hostName, endTime, startTime, eventTitle]);

  return (
    <MyScheduleIconContext.Provider
      value={{
        ...scheduleIconValues,
        registrationData: props.registrationData,
      }}
    >
      <AppShare
        {...getShareData(props.eventId, props.registrationData)}
        openReport={openReport}
        closeReport={closeReport}
      />
      {visible && (
        <div className={classnames('fixed-page', isMobileUi && 'mobile')}>
          <PageReport
            visible={visible}
            onClose={closeReport}
            endTime={endTime}
            startTime={startTime}
            eventId={props.eventId}
          />
        </div>
      )}
      <CanvasComponent
        followButton={FollowButton}
        registerButton={RegisterButtonWithSessionSelect}
        userDropdown={UserDropdown}
        footer={CustomFooter}
        {...props}
        price={isNoLimit ? null : props.price}
        schema={props.schema || canvasData?.schema || []}
        packages={props.packages || canvasData?.packages || []}
        entryLobbyButton={EntryLobbyButton}
        // 这里通过undefined来判断后台接口是否返回了数据，若没返回则为undefined
        showEntryLobby={
          showLobbyBtn !== undefined && lobbySwitch !== undefined
          ? showLobbyBtn && (lobbySwitch || isNoLimit)
          : undefined
        }
        hasAdmissionTicket={hasAdmissionTicket}
      />
      {(!lobbySwitch || isNoLimit) && (
        <MyScheduleWidget myScheduleList={props.myScheduleList} eventid={props.eventId} />
      )}
    </MyScheduleIconContext.Provider>
  );
};

const FollowButton: VFC<IProps & { className?: string }> = (props) => {
  if (!props.hostData) return null;
  const [guideVisible, subscribeBack] = useState(false);
  const { toggleFollowHost, isFollow } = hostHook(props.hostData, subscribeBack, true);
  return (
    <GuidePc
      visible={guideVisible}
      handleKnowBtn={() => {
        setJudgeShowRecord(props.hostData.host_detail.host_id);
        subscribeBack(false);
      }}
      handleNoRemind={() => {
        setJudgeShowRecord();
        subscribeBack(false);
      }}
    >
      {props.isOwner ? null : (
        <Button
          type={isFollow ? 'weak' : 'primary'}
          icon={isFollow ? <CheckIcon /> : <PlusIcon />}
          className={classnames('size-xs', props.className)}
          onClick={toggleFollowHost}
        >
          <span>{isFollow ? '已关注' : '关注'}</span>
        </Button>
      )}
    </GuidePc>
  );
};

const UserDropdown = () => {
  const { canChangeAccount, user, userData, goIndexPage } = useAccountUI();
  const { isMobile } = useContext(AppContext);
  const { pathname } = useRouter();
  const menu = getMenuList(userData, pathname, canChangeAccount);
  const [showMenu, setShowMenu] = useState(false);

  if (isMobile) {
    return (
      <>
        <UserMenu visible={showMenu} onClose={() => setShowMenu(false)} />
        <img
          className="canvas-nav-avatar ma-user__avatar"
          src={user.imageUrl}
          onClick={() => setShowMenu(true)}
        />
      </>
    );
  }

  return (
    <Dropdown
      appearance="pure"
      trigger="hover"
      placement="bottom"
      boxClassName="size-h-xs ma-user-dropdown-box"
      button={
        <img
          className="canvas-nav-avatar ma-user__avatar"
          src={user.imageUrl}
          onClick={goIndexPage}
        />
      }
    >
      {menu}
    </Dropdown>
  );
};

const EntryLobbyButton: VFC<
  IProps & {
    className?: string;
    myScheduleList: OwnSchedules;
    cover?: number;
    isMobile?: boolean;
  }
> = (props) => {
  const { isLogin } = useLogin();
  const { registrationData } = useContext(MyScheduleIconContext);
  const isNotLimit = registrationData?.activity_basic_info.access_rule === 1;
  const { lobbyType, lobbyLiveEnable } = useLobbyType({ event_id: props.eventId });
  const { lobbySwitch } = useEventGray({ event_id: props.eventId });
  const entry = useMemo(() => {
    const page = skipPage(
      props.myScheduleList,
      registrationData?.activity_basic_info?.schedules || [],
      lobbyType,
      lobbyLiveEnable,
      registrationData?.activity_basic_info || {},
    );
    if (props.myScheduleList?.length > 0 && page) {
      return `/${page}`;
    }
    return '';
  }, [props.myScheduleList, lobbyType, lobbyLiveEnable, registrationData]);
  const { cover = 1, className, isMobile = false } = props;

  const onEnterClick = useCallback(() => {
    if (!lobbySwitch && !isLogin) {
      login();
      return;
    }
    if (isLogin && !lobbySwitch && isNotLimit) {
      const myTicket = document.querySelector<HTMLDivElement>('.widget-my-schedule img');
      if (myTicket) {
        myTicket.click();
        return;
      }
    }
    openPcLobbyPage(props.eventId, entry);
  }, [lobbySwitch, isNotLimit, props.eventId, entry, isLogin]);

  if (isMobile) {
    return (
      <Button className={classnames(props.className)} onClick={onEnterClick}>
        进入活动
      </Button>
    );
  }
  const WRAP = cover === 2 ? CanvasCover2.Btn : CanvasCover1.Btn;
  return (
    <WRAP
      className={className}
      onClick={onEnterClick}
      text="进入活动"
      type="primary"
      order={1}
    ></WRAP>
  );
};

export default CanvasRender;
